/**
 * An Thư Journal — cấu hình 6 chuyên đề của khu /tin-tuc/.
 * Nội dung chữ trên trang chuyên đề (hero, thẻ, khối) lấy theo bản thiết kế
 * design/landing-page-news/an-thu-ui-tron-bo. Muốn đổi chữ / link: sửa ở file này.
 */

export type TopicSlug = 'bang-gia' | 'kiem-dinh' | 'kim-cuong' | 'nhan-cuoi' | 'trang-suc' | 'vang-bao-quan';

export type JournalArticle = {
  id: number;
  slug: string;
  title: string;
  excerpt: string;
  date: string;
  image: string | null;
  topics: TopicSlug[];
  categories?: number[];
  /** bài soạn sẵn trong repo (chưa có trên anthu.today) */
  local?: boolean;
  /** thuộc chuyên mục "Tin Tức" (khuyến mại, sự kiện) trên anthu.today */
  news?: boolean;
  press?: boolean;
  promotion?: boolean;
  source?: { href: string; label: string };
};

export type CardLink = { title: string; desc?: string; href: string; label?: string };

export type TopicBlock =
  | { type: 'cards'; cols: 3 | 4; items: CardLink[] }
  | { type: 'steps'; title: string; items: { en: string; vi: string; href: string }[] }
  | { type: 'sizePanel' }
  | { type: 'institutions'; title: string; items: string[] }
  | { type: 'readGuide'; title: string; lead: string; steps: { title: string; desc: string }[]; cta: string }
  | { type: 'careSteps'; title: string; items: { title: string; q: string }[] }
  | { type: 'productLines'; title: string; items: { title: string; href: string; image: string }[] }
  | { type: 'split'; image: string; eyebrow: string; title: string; lead: string; cta: string }
  | { type: 'banner'; tone: 'rose' | 'beige'; eyebrow?: string; title: string; lead: string; cta: string; href: string }
  | { type: 'articles'; title: string; intro?: boolean };

export type Topic = {
  slug: TopicSlug;
  /** tên ngắn dùng ở thẻ, liên kết, nhãn bài */
  title: string;
  /** nhãn viết hoa trên thẻ bài */
  tag: string;
  /** dòng mô tả dưới tên chuyên đề ở trang /tin-tuc/ */
  summary: string;
  tile: string;
  hero: {
    tone: 'beige' | 'dark' | 'rose' | 'plain';
    eyebrow: string;
    title: string[];
    lead: string;
    image?: string;
    cta?: { label: string; href: string; tone: 'green' | 'gold' };
    imageContain?: boolean;
  };
  seoTitle: string;
  seoDescription: string;
  /** hỏi đáp hiển thị trên trang + JSON-LD FAQPage; chỉ dùng thông tin đã chốt của An Thư hoặc kiến thức phổ thông */
  faq: { q: string; a: string }[];
  blocks: TopicBlock[];
  /** nhãn + từ khoá phân loại (đã bỏ dấu, chữ thường) */
  match: RegExp;
};

const APPOINTMENT = '/accounts/appointment/new';
const q = (s: string) => `/tin-tuc/?q=${encodeURIComponent(s)}`;

export const JOURNAL_TOPICS: Topic[] = [
  {
    slug: 'bang-gia',
    title: 'Bảng giá',
    tag: 'BẢNG GIÁ',
    summary: 'Giá vàng · Tỷ giá ngoại tệ · Kim cương',
    tile: '/journal/tile-bang-gia.jpg',
    hero: { tone: 'beige', eyebrow: '', title: [], lead: '' },
    seoTitle: '',
    seoDescription: '',
    faq: [],
    blocks: [],
    match: /^$/,
  },
  {
    slug: 'kiem-dinh',
    title: 'Kiểm định',
    tag: 'KIỂM ĐỊNH',
    summary: 'GIA · IGI · Liulab · DOJI · SJC',
    tile: '/journal/tile-kiem-dinh.jpg',
    hero: {
      tone: 'beige',
      eyebrow: 'KIỂM ĐỊNH KIM CƯƠNG',
      title: ['An tâm từ', 'những thông tin rõ ràng.'],
      lead: 'Tìm hiểu các đơn vị kiểm định và cách đọc chứng thư trước khi lựa chọn kim cương.',
      image: '/journal/chung-thu.jpg',
      cta: { label: 'Tư vấn cùng An Thư', href: APPOINTMENT, tone: 'green' },
      imageContain: true,
    },
    seoTitle: 'Kiểm định kim cương: đọc giấy chứng thư',
    seoDescription:
      'Cách đọc giấy kiểm định kim cương, tìm hiểu GIA, IGI, Liulab, DOJI, SJC trước khi mua. Kiến thức từ An Thư The Diamond Store, TP.HCM.',
    faq: [
      { q: 'Kim cương tại An Thư có giấy kiểm định của đơn vị nào?', a: 'Mỗi viên kim cương chủ tại An Thư có giấy kiểm định của Liulab, DOJI hoặc SJC. Số giấy kiểm định được in ngay trên trang sản phẩm để bạn đối chiếu.' },
      { q: 'Giấy kiểm định kim cương cho biết những gì?', a: 'Giấy kiểm định ghi đơn vị cấp, mã số chứng thư, hình dạng và giác cắt, kích thước, trọng lượng (carat), cấp độ màu và độ tinh khiết của viên kim cương. Đây là cơ sở để so sánh các viên với nhau trước khi quyết định mua.' },
      { q: 'Tôi chưa hiểu giấy kiểm định thì hỏi ai?', a: 'An Thư có một showroom duy nhất tại 89A Nguyễn Trãi, Phường Bến Thành, TP. Hồ Chí Minh, mở cửa 09:00–21:00 hằng ngày. Hotline 033 333 6789. Bạn có thể mang giấy kiểm định đến để được tư vấn trực tiếp.' },
    ],
    blocks: [
      { type: 'institutions', title: 'Tìm hiểu đơn vị kiểm định', items: ['GIA', 'IGI', 'Liulab', 'DOJI', 'SJC'] },
      {
        type: 'readGuide',
        title: 'Cách đọc giấy kiểm định',
        lead: 'Đi từ thông tin nhận diện đến các thông số chất lượng.',
        steps: [
          { title: 'Thông tin nhận diện', desc: 'Đơn vị cấp, mã chứng thư và thông tin viên kim cương.' },
          { title: 'Các tiêu chí chất lượng', desc: 'Kích thước, trọng lượng và các mục đánh giá trên giấy.' },
          { title: 'Đối chiếu & tư vấn', desc: 'Đọc hướng dẫn của An Thư hoặc mang giấy để được hỗ trợ.' },
        ],
        cta: 'Đọc hướng dẫn kiểm định',
      },
      { type: 'articles', title: 'Bài viết về kiểm định' },
      {
        type: 'cards',
        cols: 3,
        items: [
          { title: 'Giấy của tôi thuộc đơn vị nào?', desc: 'Xem phần hỏi đáp của An Thư.', href: '/hoi-dap/', label: 'Khám phá' },
          { title: 'Giá & chất lượng', desc: 'Kết nối kiểm định với lựa chọn.', href: '/bang-gia/', label: 'Khám phá' },
          { title: 'Tìm hiểu kim cương', desc: 'Khám phá 4C và các giác cắt.', href: '/tin-tuc/kien-thuc-kim-cuong/', label: 'Khám phá' },
        ],
      },
    ],
    match: /kiem dinh|chung thu|giay chung nhan|liulab|doji|\bsjc\b/,
  },
  {
    slug: 'kim-cuong',
    title: 'Kim cương',
    tag: 'KIM CƯƠNG',
    summary: '4C · Li & carat · Tự nhiên & nhân tạo',
    tile: '/journal/tile-kim-cuong.jpg',
    hero: {
      tone: 'dark',
      eyebrow: 'THẾ GIỚI KIM CƯƠNG',
      title: ['Vẻ đẹp bắt đầu', 'từ sự thấu hiểu.'],
      lead: 'Khám phá 4C, kích thước, nguồn gốc và những sắc thái của trang sức đá quý.',
      image: '/journal/hero-ring.jpg',
      cta: { label: 'Tư vấn cùng An Thư', href: APPOINTMENT, tone: 'gold' },
    },
    seoTitle: 'Kim cương: 4C, carat, tự nhiên và nhân tạo',
    seoDescription:
      'Tìm hiểu 4C (giác cắt, màu sắc, độ tinh khiết, carat), kim cương tự nhiên và nhân tạo. Kiến thức từ An Thư The Diamond Store, TP.HCM.',
    faq: [
      { q: '4C của kim cương là gì?', a: '4C là bốn tiêu chí đánh giá kim cương: Cut (giác cắt), Color (màu sắc), Clarity (độ tinh khiết) và Carat (trọng lượng). Hai viên cùng carat vẫn có thể khác giá nếu khác ở ba tiêu chí còn lại.' },
      { q: 'Li và carat khác nhau thế nào?', a: 'Li (milimét) là kích thước, thường là đường kính viên đá nhìn từ trên xuống. Carat là trọng lượng, 1 carat bằng 0,2 gram. Viên tròn 1 carat thường có đường kính khoảng 6,4–6,5 mm; kích thước thực tế còn phụ thuộc giác cắt.' },
      { q: 'An Thư bán kim cương tự nhiên hay nhân tạo?', a: 'An Thư bán trang sức kim cương tự nhiên, mỗi viên chủ có giấy kiểm định Liulab, DOJI hoặc SJC. Nhẫn có giá từ 8 đến 300 triệu đồng.' },
    ],
    blocks: [
      {
        type: 'steps',
        title: 'Bắt đầu với 4C',
        items: [
          { en: 'Cut', vi: 'Giác cắt', href: q('giác cắt') },
          { en: 'Color', vi: 'Màu sắc', href: q('màu sắc') },
          { en: 'Clarity', vi: 'Độ tinh khiết', href: q('tinh khiết') },
          { en: 'Carat', vi: 'Trọng lượng', href: q('carat') },
        ],
      },
      { type: 'sizePanel' },
      {
        type: 'cards',
        cols: 3,
        items: [
          { title: 'Tự nhiên & nhân tạo', desc: 'Tìm hiểu nguồn gốc và cách lựa chọn.', href: q('nhân tạo'), label: 'Khám phá' },
          { title: 'Ngọc trai & đá quý', desc: 'Tư vấn chất liệu và sắc màu.', href: '/danh-muc/ngoc-trai-da-quy/', label: 'Khám phá' },
          { title: 'Hiểu giấy kiểm định', desc: 'Đối chiếu các tiêu chí của từng viên.', href: '/tin-tuc/kiem-dinh/', label: 'Khám phá' },
        ],
      },
      { type: 'articles', title: 'Khám phá sâu hơn', intro: true },
    ],
    match: /4c|giac cat|tinh khiet|cap do mau|mau sac|carat|huynh quang|tu nhien|nhan tao|lab-?grown|kim cuong tu nhien/,
  },
  {
    slug: 'nhan-cuoi',
    title: 'Nhẫn cưới',
    tag: 'NHẪN CƯỚI & ĐÍNH HÔN',
    summary: 'Cầu hôn · Nhẫn cưới · Đo ni · Ngân sách',
    tile: '/journal/tile-nhan-cuoi.jpg',
    hero: {
      tone: 'rose',
      eyebrow: 'CẦU HÔN & NHẪN CƯỚI',
      title: ['Một lời hẹn.', 'Một đời nâng niu.'],
      lead: 'Chọn chiếc nhẫn dành cho câu chuyện của hai bạn, từ khoảnh khắc cầu hôn đến ngày cưới.',
      image: '/journal/hero-princess.jpg',
      cta: { label: 'Tư vấn cùng An Thư', href: APPOINTMENT, tone: 'green' },
    },
    seoTitle: 'Nhẫn cưới, nhẫn cầu hôn: đo ni và ngân sách',
    seoDescription:
      'Gợi ý chọn nhẫn cầu hôn, nhẫn cưới kim cương, cách đo ni và cân đối ngân sách. Cảm hứng từ An Thư The Diamond Store, TP.HCM.',
    faq: [
      { q: 'Nhẫn kim cương tại An Thư có giá từ bao nhiêu?', a: 'Nhẫn kim cương tại An Thư có giá từ 8 đến 300 triệu đồng, giá niêm yết công khai trên anthu.vn.' },
      { q: 'An Thư có sửa ni nhẫn miễn phí không?', a: 'An Thư sửa ni miễn phí trong 3 ni. Các trường hợp ngoài mức này được tính phí theo giá.' },
      { q: 'Đặt cọc nhẫn tại An Thư như thế nào?', a: 'Mức đặt cọc tối thiểu là 10% giá trị sản phẩm và phần còn lại được thanh toán trong 3 ngày. Nếu hủy, khoản cọc sẽ không được hoàn lại. Chi tiết tại trang chính sách đặt cọc của An Thư.' },
    ],
    blocks: [
      {
        type: 'cards',
        cols: 4,
        items: [
          { title: 'Nhẫn cầu hôn', desc: 'Khám phá cảm hứng đính hôn.', href: q('cầu hôn'), label: 'Khám phá' },
          { title: 'Nhẫn cưới', desc: 'Tham khảo các thiết kế để giới thiệu.', href: q('nhẫn cưới'), label: 'Khám phá' },
          { title: 'Đo ni nhẫn', desc: 'Hướng dẫn kích thước & quy đổi size.', href: '/guide-to-measuring-hands/', label: 'Khám phá' },
          { title: 'Ngân sách', desc: 'Tư vấn để chọn chiếc nhẫn phù hợp.', href: q('ngân sách'), label: 'Khám phá' },
        ],
      },
      {
        type: 'split',
        image: '/journal/heart-ring.jpg',
        eyebrow: 'KHOẢNH KHẮC CẦU HÔN',
        title: 'Dấu ấn riêng\ncho lời yêu của bạn.',
        lead: 'Tìm cảm hứng từ giác cắt Heart và những thiết kế An Thư đã giới thiệu.',
        cta: 'Khám phá cảm hứng',
      },
      {
        type: 'banner',
        tone: 'rose',
        title: 'Vừa vặn trên tay. Phù hợp trong lựa chọn.',
        lead: 'Đo ni tại cửa hàng và trao đổi ngân sách để cùng An Thư tìm chiếc nhẫn dành cho hai bạn.',
        cta: 'Hướng dẫn đo ni',
        href: '/guide-to-measuring-hands/',
      },
      { type: 'articles', title: 'Cảm hứng cho lời hẹn' },
    ],
    match: /nhan cuoi|cau hon|dinh hon|do ni|size nhan|nhan doi|top 10 mau nhan|heart|emerald|princess|round cut/,
  },
  {
    slug: 'trang-suc',
    title: 'Trang sức',
    tag: 'TRANG SỨC & QUÀ TẶNG',
    summary: 'Phong cách · Quà tặng · Phong thuỷ',
    tile: '/journal/tile-trang-suc.jpg',
    hero: {
      tone: 'plain',
      eyebrow: 'TRANG SỨC AN THƯ',
      title: ['Dấu ấn của riêng bạn.'],
      lead: 'Khám phá trang sức qua phong cách, dịp tặng và những điều bạn trân quý.',
    },
    seoTitle: 'Trang sức kim cương: phong cách và quà tặng',
    seoDescription:
      'Cảm hứng chọn trang sức kim cương theo phong cách, dịp tặng quà và quan niệm phong thuỷ. Bài viết từ An Thư The Diamond Store, TP.HCM.',
    faq: [
      { q: 'Showroom An Thư ở đâu, mở cửa lúc nào?', a: 'An Thư có một showroom duy nhất tại 89A Nguyễn Trãi, Phường Bến Thành, TP. Hồ Chí Minh, mở cửa 09:00–21:00 hằng ngày. Hotline 033 333 6789.' },
      { q: 'Không đến showroom được thì mua trang sức An Thư thế nào?', a: 'Bạn có thể mua qua website anthu.vn, app An Thư hoặc livestream, và nhận hàng giao tận nơi trên toàn quốc.' },
      { q: 'Chính sách thu đổi trang sức của An Thư ra sao?', a: 'Đổi lớn hoặc đổi ngang được tính 0%. Khi thu lại, An Thư trừ 25% với giá trị dưới 200 triệu, 20% với từ 200 đến dưới 500 triệu và 18% với từ 500 triệu trở lên. Chi tiết tại trang chính sách thu đổi.' },
    ],
    blocks: [
      {
        type: 'productLines',
        title: 'Chọn dòng trang sức',
        items: [
          { title: 'Nhẫn nữ', href: '/danh-muc/nhan-nu/', image: '/journal/line-1.jpg' },
          { title: 'Bông tai', href: '/danh-muc/bong-tai/', image: '/journal/line-2.jpg' },
          { title: 'Mặt dây', href: '/danh-muc/mat-day-chuyen/', image: '/journal/line-3.jpg' },
          { title: 'Vòng & lắc', href: '/danh-muc/vong-tay/', image: '/journal/line-4.jpg' },
          { title: 'Trang sức nam', href: '/danh-muc/trang-suc-nam/', image: '/journal/line-5.jpg' },
        ],
      },
      {
        type: 'cards',
        cols: 3,
        items: [
          { title: 'Quà tặng', desc: 'Tìm cảm hứng cho người bạn yêu thương.', href: q('quà tặng'), label: 'Khám phá' },
          { title: 'Trang sức nam', desc: 'Khám phá lựa chọn từ An Thư.', href: '/danh-muc/trang-suc-nam/', label: 'Khám phá' },
          { title: 'Phong thuỷ', desc: 'Câu chuyện & quan niệm về trang sức.', href: q('phong thủy'), label: 'Khám phá' },
        ],
      },
      { type: 'articles', title: 'Cảm hứng & câu chuyện trang sức', intro: true },
    ],
    match: /trang suc|qua tang|phong thuy|xu huong|bong tai|toi gian|ngoc cam thach|da quy/,
  },
  {
    slug: 'vang-bao-quan',
    title: 'Vàng & bảo quản',
    tag: 'BẢO QUẢN',
    summary: 'Chất liệu · Chăm sóc · Sửa chữa',
    tile: '/journal/tile-vang-bao-quan.jpg',
    hero: {
      tone: 'beige',
      eyebrow: 'CHẤT LIỆU & CHĂM SÓC',
      title: ['Gìn giữ vẻ đẹp', 'qua năm tháng.'],
      lead: 'Tìm hiểu chất liệu, cách bảo quản và dịch vụ chăm sóc trang sức tại An Thư.',
      image: '/journal/hero-bracelet.jpg',
      cta: { label: 'Tư vấn cùng An Thư', href: APPOINTMENT, tone: 'green' },
    },
    seoTitle: 'Bảo quản trang sức vàng, kim cương đúng cách',
    seoDescription:
      'Hướng dẫn bảo quản, làm sạch trang sức vàng, kim cương và đá quý tại nhà, cùng thông tin sửa ni từ An Thư The Diamond Store, TP.HCM.',
    faq: [
      { q: 'Làm sạch trang sức kim cương tại nhà thế nào?', a: 'Ngâm trang sức vài phút trong nước ấm pha xà phòng dịu, chải nhẹ bằng bàn chải mềm, tráng lại bằng nước sạch rồi lau khô bằng khăn mềm. Tránh dùng hóa chất mạnh như chất tẩy chứa clo.' },
      { q: 'An Thư có sửa ni miễn phí không?', a: 'An Thư sửa ni miễn phí trong 3 ni. Các trường hợp ngoài mức này được tính phí theo giá.' },
      { q: 'Mang trang sức đến đâu để được kiểm tra và tư vấn?', a: 'An Thư có một showroom duy nhất tại 89A Nguyễn Trãi, Phường Bến Thành, TP. Hồ Chí Minh, mở cửa 09:00–21:00 hằng ngày. Hotline 033 333 6789.' },
    ],
    blocks: [
      {
        type: 'cards',
        cols: 3,
        items: [
          { title: 'Vàng 14K · 18K · 24K', desc: 'Tìm hiểu trước khi chọn chất liệu.', href: q('vàng'), label: 'Khám phá' },
          { title: 'Vàng trắng & bạch kim', desc: 'Tham khảo bài viết về mặt dây.', href: q('vàng trắng'), label: 'Khám phá' },
          { title: 'Kim cương & đá quý', desc: 'Chọn cách chăm sóc theo món trang sức.', href: q('đá quý'), label: 'Khám phá' },
        ],
      },
      {
        type: 'careSteps',
        title: 'Chăm sóc bắt đầu từ thói quen.',
        items: [
          { title: 'Bảo quản tại nhà', q: 'bảo quản' },
          { title: 'Làm sạch trang sức', q: 'làm sạch' },
          { title: 'Chăm sóc đá quý', q: 'đá quý' },
        ],
      },
      { type: 'articles', title: 'Hướng dẫn từ An Thư' },
      {
        type: 'banner',
        tone: 'beige',
        eyebrow: 'SỬA CHỮA & BẢO HÀNH',
        title: 'Mang trang sức đến An Thư.',
        lead: 'Trao đổi tình trạng sản phẩm để được tư vấn dịch vụ phù hợp.',
        cta: 'Xem chính sách bảo hành',
        href: '/policies/product-warranty/',
      },
    ],
    match: /bao quan|lam sach|ve sinh|vang 14|vang 18|vang 24|vang trang|sua ni|bao hanh|cham soc|siet chau|danh bong/,
  },
];

export const TOPIC_BY_SLUG: Record<string, Topic> = JOURNAL_TOPICS.reduce(
  (acc, t) => ({ ...acc, [t.slug]: t }),
  {} as Record<string, Topic>
);

export const PAGE_TOPICS = JOURNAL_TOPICS.filter((t) => t.slug !== 'bang-gia');

/** Navigation labels are shared by desktop and mobile Journal headers. */
export const NAV_ITEMS = [
  { label: 'Kiến Thức Kim Cương', href: '/tin-tuc/kien-thuc-kim-cuong/', key: 'kim-cuong' },
  { label: 'Kiến Thức Trang Sức', href: '/tin-tuc/kien-thuc-trang-suc/', key: 'trang-suc' },
  { label: 'Tin Tức An Thư', href: '/tin-tuc/tin-an-thu/', key: 'tin-an-thu' },
  { label: 'Báo Chí', href: '/tin-tuc/bao-chi/', key: 'bao-chi' },
  { label: 'Khuyến Mãi', href: '/tin-tuc/khuyen-mai/', key: 'khuyen-mai' },
  { label: 'Bảng Giá', href: '/bang-gia/', key: 'bang-gia' },
];

/** Public paths follow the current WordPress category slugs. */
export const topicHref = (slug: string) => `/tin-tuc/${slug === 'kim-cuong' ? 'kien-thuc-kim-cuong' : slug === 'trang-suc' ? 'kien-thuc-trang-suc' : slug}/`;
